import {ISavedSearch, ISavedSearchConcept} from 'app/models/saved_search'
import {DiscoveryRequest, DiscoveryRequestConcept} from '../_models'
import {mapAndSort} from './_utils'
import {CConcept} from './concept'
import {CBaseRecord} from './_base'
import {JsonObject} from 'utilities/types'

export enum AlgorithmType {
  standard = 'standard',
  extended = 'extended',
}

export class CSavedSearchConcept extends CConcept implements ISavedSearchConcept {
  __weight
  __lock

  constructor(data: ISavedSearchConcept & JsonObject) {
    super(data)
    this.__weight = data.__weight ?? 1
    this.__lock = data.__lock ?? null
  }

  toIDiscoveryRequestConcept() {
    return {
      unique_id: this.id,
      graph_id: this.id,
      concept_text: this.name,
      prioritization_order: 0,
      datetime_excluded_as_unrelated: this.__lock === 'exclude' ? 'ignore' : null,
      is_keyword: false,
      weight: this.__weight,
    }
  }

  toDiscoveryRequestConcept() {
    return new DiscoveryRequestConcept(this.toIDiscoveryRequestConcept())
  }

  static fromDiscoveryRequestConcept(con: DiscoveryRequestConcept) {
    if (con.is_keyword) {
      // This is a keyword.
      return con.concept_text
    } else {
      // This is a weighted concept.
      return new CSavedSearchConcept({
        id: con.graph_id,
        aliases: [],
        name: con.concept_text,
        qid: '',
        score: 0,
        __weight: con.weight ?? 1,
        __lock: con.excluded_as_unrelated ? 'exclude' : null,
      })
    }
  }

  stringify() {
    return JSON.stringify(this.export())
  }

  export() {
    return {
      id: this.id,
      __weight: this.__weight,
      __lock: this.__lock,
      aliases: this.aliases,
      name: this.name,
      qid: this.qid,
      score: this.score,
    }
  }
}

export class CSavedSearch extends CBaseRecord<ISavedSearch> implements ISavedSearch {
  static record_name = 'saved_search'
  static external_id = 'saved_search_id'
  // due to some quirks of how the API is built, we do not treat "concepts" as a child of this class.

  project_id
  name
  search_text
  keywords
  concepts
  algorithm

  constructor(data: (ISavedSearch & JsonObject) | null) {
    const dataOrDefs = data ?? {
      id: undefined,
      search_text: '',
      project_id: null,
      name: null,
      keywords: [],
      concepts: [],
      algorithm: AlgorithmType.standard,
    }
    super(dataOrDefs)

    this.id = dataOrDefs.id
    this.name = dataOrDefs.name ?? null
    this.project_id = dataOrDefs.project_id ?? null
    this.search_text = dataOrDefs.search_text ?? ''
    this.keywords = dataOrDefs.keywords ?? []
    this.concepts =
      dataOrDefs.concepts?.map(
        (x) => new CSavedSearchConcept(x as unknown as ISavedSearchConcept & JsonObject)
      ) ?? []
    this.algorithm = (dataOrDefs.algorithm as AlgorithmType | undefined) ?? AlgorithmType.standard
  }

  toDiscoveryRequest() {
    const concepts = this.concepts.map((x) => x.toIDiscoveryRequestConcept())
    const keywords = this.keywords.map((keyword) => CSavedSearch.keywordToIDiscoveryRequestConcept)

    return new DiscoveryRequest({
      created_at: this.created?.at,
      updated_at: this.updated?.at,
      unique_id: this.id,
      inputted_text_from_user: this.search_text,
      latest_inputted_text_from_user: this.search_text,
      revision_count: 0,
      active_concepts: [...concepts, ...keywords],
      summary_of_inputted_text: this.name ?? this.search_text?.slice(0, 200),
      included_concepts_string: this.concepts
        .filter((x) => x.__lock !== 'exclude')
        .map((x) => x.name)
        .join(', '),
      likely_company_search: false,
    })
  }

  get saved() {
    return this.id !== null
  }

  get routes() {
    if (this.saved) {
      return {
        results: `/discovery/${this.id}/results`,
        analyze: `/discovery/${this.id}`,
      }
    }
    return {
      results: null,
      analyze: `/discovery`,
    }
  }

  addConcept(concept: ISavedSearchConcept & JsonObject) {
    this.concepts.push(new CSavedSearchConcept(concept))
  }

  addKeyword(keyword: string) {
    const downcased = keyword.toLowerCase()
    if (!this.keywords.includes(downcased)) {
      this.keywords.push(keyword)
    }
  }

  // Returns a string representation of this object, suitable for putting in a dependency array.
  stringify() {
    return JSON.stringify(this.export())
  }

  // Returns a stripped-down JSON form of this saved search. This can be sent to API queries, and it also
  // forms the basis of the "stringified" prop above.
  // Note that export() strips out everything the API does not care about (e.g., concept names).
  export() {
    // Use map() to create a copy of our keywords array. If we did not do this, sort() would
    // change the order of the actual keywords array, with unpredictable results in the UI.
    const keywords: string[] = this.keywords.map((x) => x)
    keywords.sort()

    const concepts = mapAndSort(this.concepts, (x) => x.export(), 'id')

    if (this.id === null) {
      const output = {
        name: this.name,
        search_text: this.search_text,
        algorithm: this.algorithm,
        keywords,
        concepts,
      } as ISavedSearch & JsonObject
      if (this.project_id !== null) {
        output.project_id = this.project_id
      }
      return output
    } else {
      const output = {
        id: this.id,
        name: this.name,
        search_text: this.search_text,
        algorithm: this.algorithm,
        keywords,
        concepts,
      } as ISavedSearch & JsonObject
      if (this.project_id !== null) {
        output.project_id = this.project_id
      }
      return output
    }
  }

  static fromDiscoveryRequest(
    req: DiscoveryRequest,
    algorithm: AlgorithmType,
    changes?: Partial<ISavedSearch>
  ) {
    const data = {
      id: changes?.id ?? req.unique_id,
      project_id: changes?.project_id ?? null,
      name: changes?.name ?? req.summary_of_inputted_text,
      search_text: changes?.search_text ?? req.latest_inputted_text_from_user,
      concepts:
        changes?.concepts ??
        (req.active_concepts
          .filter((x) => !x.is_keyword)
          .map(CSavedSearchConcept.fromDiscoveryRequestConcept) as CSavedSearchConcept[]),
      keywords:
        changes?.keywords ??
        (req.active_concepts
          .filter((x) => x.is_keyword)
          .map(CSavedSearchConcept.fromDiscoveryRequestConcept) as string[]),
      algorithm,
    }
    return new CSavedSearch(data as unknown as ISavedSearch & JsonObject)
  }

  static keywordToIDiscoveryRequestConcept(keyword: string) {
    return {
      unique_id: keyword,
      graph_id: keyword,
      concept_text: keyword,
      prioritization_order: 0,
      datetime_excluded_as_unrelated: null,
      is_keyword: true,
    }
  }
}
